import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Checkbox,
  Typography,
  Button,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  IconButton,
  TableSortLabel,
} from "@mui/material";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

function IssuedInvoiceTable({
  issued,
  issuedLoading,
  selectedRows,
  handleSelectRow,
  handleSelectAll,
  issuedPage,
  issuedRowsPerPage,
  handleIssuedPageChange,
  handleIssuedRowsPerPageChange,
  hasMixedInvoiceTypes,
  dealer,
  selectedIssuedDealer,
  handleIssuedDealerChange,
  actionAnchorEl,
  setActionAnchorEl,
  allPaid,
  setOpenModal,
  setOpenReceiptModal,
  sortBy,
  sortOrder,
  handleSortChange,
}) {
  return (
    <div className="card mb-4 shadow-sm">
      <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center py-3">
        <h4 className="mb-0 fw-bold">Invoice List Issued</h4>
        <div className="d-flex align-items-center">
          <Button
            component={Link}
            to="/app/invoice-generator"
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            className="me-2"
            sx={{
              backgroundColor: "#28a745",
              "&:hover": {
                backgroundColor: "#218838",
                transform: "scale(1.05)",
                transition: "all 0.2s ease-in-out",
              },
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: 2,
              boxShadow: 2,
            }}
          >
            Create Invoice
          </Button>
          {selectedRows.length > 0 && (
            <Button
              variant="contained"
              color="secondary"
              onClick={(e) => setActionAnchorEl(e.currentTarget)}
              sx={{
                minWidth: "120px",
                textTransform: "none",
                fontWeight: "bold",
                borderRadius: 2,
                boxShadow: 2,
              }}
              disabled={hasMixedInvoiceTypes}
            >
              Actions ({selectedRows.length})
            </Button>
          )}
        </div>
        <Menu
          anchorEl={actionAnchorEl}
          open={Boolean(actionAnchorEl)}
          onClose={() => setActionAnchorEl(null)}
          PaperProps={{
            sx: {
              boxShadow: 3,
              borderRadius: 2,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setOpenModal(true);
              setActionAnchorEl(null);
            }}
          >
            <span className="d-flex align-items-center">
              <span
                className="material-icons me-2"
                style={{ fontSize: "1rem" }}
              >
                check_circle
              </span>
              Save as Paid
            </span>
          </MenuItem>
          {allPaid && (
            <MenuItem
              onClick={() => {
                setOpenReceiptModal(true);
                setActionAnchorEl(null);
              }}
            >
              <span className="d-flex align-items-center">
                <span
                  className="material-icons me-2"
                  style={{ fontSize: "1rem" }}
                >
                  receipt
                </span>
                Send Receipt
              </span>
            </MenuItem>
          )}
        </Menu>
      </div>
      <div className="card-body">
        <div className="row mb-4 align-items-center">
          <div className="col-md-4">
            <FormControl fullWidth>
              <InputLabel id="dealer-select-label" sx={{ color: "#ffffff" }}>
                Select Dealer
              </InputLabel>
              <Select
                labelId="dealer-select-label"
                value={selectedIssuedDealer}
                onChange={handleIssuedDealerChange}
                sx={{
                  color: "#ffffff",
                  backgroundColor: "#333333",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ffffff",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ffffff",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ffffff",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#ffffff",
                  },
                  borderRadius: 1,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "#ffffff",
                      color: "#000000",
                      "& .MuiMenuItem-root": {
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="">All Dealers</MenuItem>
                {dealer &&
                  dealer.map((d) => (
                    <MenuItem key={d._id} value={d._id}>
                      {d.companyName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </div>
        </div>
        {hasMixedInvoiceTypes && selectedRows.length > 0 && (
          <Typography color="error" sx={{ mb: 2 }}>
            Cannot select both Custom and Order-Based invoices for payment.
          </Typography>
        )}
        {issued.data.some(
          (invoice) => invoice.remainingBalance === undefined
        ) && (
          <Typography color="warning" sx={{ mb: 2 }}>
            Warning: Some invoices are missing remaining balance data.
            Displaying total instead.
          </Typography>
        )}
        {issuedLoading ? (
          <div className="d-flex justify-content-center py-5">
            <CircularProgress />
          </div>
        ) : (
          <Paper elevation={0} variant="outlined">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedRows.length > 0 &&
                          selectedRows.length < issued.data.length
                        }
                        checked={
                          issued.data.length > 0 &&
                          issued.data.every((row) =>
                            selectedRows.includes(row._id)
                          )
                        }
                        onChange={() => handleSelectAll(issued.data)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Invoice Number
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      <TableSortLabel
                        active={sortBy === "invoiceDate"}
                        direction={sortOrder}
                        onClick={() => handleSortChange("invoiceDate")}
                        IconComponent={sortOrder === "asc" ? ArrowUpwardIcon : ArrowDownwardIcon}
                      >
                        Invoice Date
                        {sortBy === "invoiceDate" && (
                          <IconButton size="small" sx={{ ml: 1 }}>
                            {sortOrder === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      <TableSortLabel
                        active={sortBy === "dueDate"}
                        direction={sortOrder}
                        onClick={() => handleSortChange("dueDate")}
                        IconComponent={sortOrder === "asc" ? ArrowUpwardIcon : ArrowDownwardIcon}
                      >
                        Due Date
                        {sortBy === "dueDate" && (
                          <IconButton size="small" sx={{ ml: 1 }}>
                            {sortOrder === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>SubTotal</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Remaining Balance
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Buyer</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Invoice Type
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {issued.data.map((invoice) => (
                    <TableRow
                      key={invoice._id}
                      hover
                      selected={selectedRows.includes(invoice._id)}
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: "#f9f9f9" },
                        "&.Mui-selected": { backgroundColor: "#e3f2fd" },
                        "&.Mui-selected:hover": { backgroundColor: "#d0e8fd" },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.includes(invoice._id)}
                          onChange={() => handleSelectRow(invoice._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <span className="fw-semibold">
                          {invoice.invoiceNumber}
                        </span>
                      </TableCell>
                      <TableCell>
                        {new Date(invoice.invoiceDate).toLocaleDateString(
                          "en-US",
                          { timeZone: "UTC" }
                        )}{" "}
                      </TableCell>
                      <TableCell>
                        {new Date(invoice.dueDate).toLocaleDateString("en-US", {
                          timeZone: "UTC",
                        })}
                      </TableCell>
                      <TableCell>${invoice.subTotal}</TableCell>
                      <TableCell>
                        <span className="fw-bold">
                          ${invoice.total.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="fw-bold">
                          {typeof invoice.remainingBalance === "number"
                            ? `$${invoice.remainingBalance.toFixed(2)}`
                            : "N/A"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="mb-1 fw-bold">
                          {invoice.buyer.companyName}
                        </div>
                        <div className="small text-muted">
                          {invoice.buyer.contact.name}
                        </div>
                        <div className="small text-muted">
                          {invoice.buyer.contact.email}
                        </div>
                        <div className="small text-muted">
                          {invoice.buyer.contact.phone}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.type}
                          size="small"
                          sx={{
                            backgroundColor:
                              invoice.type === "Custom" ? "#fff3cd" : "#cfe2ff",
                            color:
                              invoice.type === "Custom" ? "#856404" : "#0d6efd",
                            fontWeight: "medium",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.status}
                          size="small"
                          sx={{
                            backgroundColor:
                              invoice.status === "Issued"
                                ? "#e2e3e5"
                                : "#d1e7dd",
                            color:
                              invoice.status === "Issued"
                                ? "#383d41"
                                : "#0f5132",
                            fontWeight: "medium",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          component={Link}
                          to={`/invoice/${invoice._id}`}
                          variant="outlined"
                          color="primary"
                          size="small"
                          sx={{
                            textTransform: "none",
                            borderRadius: 1,
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={issued.totalInvoices}
              page={issuedPage}
              onPageChange={handleIssuedPageChange}
              rowsPerPage={issuedRowsPerPage}
              onRowsPerPageChange={handleIssuedRowsPerPageChange}
              sx={{ borderTop: "1px solid #e0e0e0" }}
            />
          </Paper>
        )}
      </div>
    </div>
  );
}

export default IssuedInvoiceTable;